// 剣舞の新しい技「引き寄せ」(2026-10-25・開発版 ?bladePull=1)。アルレッキーノ (Lies of P) の、鉤で手もとへ引き寄せてから斬る技を落とし込んだ。
//   作者「剣舞の技の数は全然増やしていいよ。ひとまずアルレッキーノを剣舞に落とし込む形で作ってもらって、実プレイしながら調整していきたい。」
//   技の組み立て = docs/research/2026-10-25-arlecchino-attack-analysis.md (引き寄せ C)。鉤の代わりに帯の布を投げ、舞踏の「手を取って引き寄せる」形にした。
//   流れ: 踊りに誘うように手を差し伸べる → 前へ鋭く放つ (藤色の布が飛ぶ・1発目) → 当たると手もとへ引き寄せる (傷は負わせない)
//         → 放された直後に、低く深く踏み込んで足もとを払う (2発目) → 立ち姿。
//   2発とも弾ける。布を弾けば引き寄せられない。布に捕まっても、放されてから払いまで 0.3 秒ほどあるので弾くか転がって抜ける。
//   絵 = public/sprites/bosses/blade-dancer/m1025_c_{1,2,3,5}.png (横の基準は sprites.ts の剣舞の表)。画面 px は絵の画素 × 0.2718 (実機の loadedBossSpriteArtPlacement で測った)。
//   布の出る手 (画面 px・足元から前, 上): 放つ c2 (124, 118)・引き戻す c3 (43, 96)。払いの剣先 c5 (190, 21)。
import { ATTACKS } from './attacks'
import { PLAYER_DAMAGE } from './balance'
import { BOSS_DEFINITIONS } from './bosses'
import { BOSS_ATTACK_TIMELINES, type BossAttackTimeline } from './boss_pose_timeline'
import { SMEAR_ARTS, preloadBladePetals, type ContactSmearArc } from './render'
import type { AttackDef, GrabSpec } from './types'
import { BLADE_SMEAR_BASE, bladeDancerV6Smear, TENSE, withDissolves, type BladeTrailStyle } from './blade_dancer_art'

const BOSS_ID = 'blade-dancer'
export { BLADE_PULL_ID } from './blade_dancer_pull_ribbon'
import { BLADE_PULL_ID, RIBBON_REACH_PX, THROW_AT } from './blade_dancer_pull_ribbon'
const C = (n: number) => `m1025_c_${n}`
const SETTLE = 'm1020_waltz_s2'
const STAND = 'm1020_waltz_c6'

const TICK = 16
const ACTIVE_MS = 130
const SECOND_AFTER_MS = 690          // 1発目から払いまで
export const BLADE_PULL_GAPS_MS = [SECOND_AFTER_MS - ACTIVE_MS - TICK]
const CHARGE_MS = 1100
const RECOVERY_MS = 950

// 掴み: 手もとまで 0.26 秒で寄せ、0.38 秒で放す (傷は負わせない・突き飛ばさない)。放された後、払いまで約 0.31 秒。
//   寄せて止める所は足元から前へ 130 (c3 の拳 43 と、前へ構えた剣先 127 より前・払いの届く 205 より内)。
const BLADE_PULL_HOLD_FWD_PX = 130
export const BLADE_PULL_GRAB: GrabSpec = { holdMs: 380, damage: 0, releaseKnockbackPx: 0, pullMs: 260, anchorFwdPx: BLADE_PULL_HOLD_FWD_PX }

const SWEEP_TIP_FWD_PX = 190
const REACH_FIT_MARGIN_PX = 15
const REAR_PX = -100
export const BLADE_PULL_STRIKES = [
  { localFromPx: 30, localToPx: RIBBON_REACH_PX },
  { localFromPx: REAR_PX, localToPx: SWEEP_TIP_FWD_PX + REACH_FIT_MARGIN_PX },
] as const
const APPROACH_PX = 380

export const BLADE_PULL_TIMELINE: BossAttackTimeline = withDissolves([
  { phase: 'telegraph', at: 0, image: SETTLE, hold: true, tag: 'anticipation', soft: true },
  { phase: 'telegraph', at: 0.08, image: C(1), hold: true, ease: 'easeIn' },                 // 踊りに誘うように手を差し伸べる
  { phase: 'telegraph', at: 0.6, image: C(1), hold: true, ...TENSE },
  { phase: 'telegraph', at: THROW_AT, image: C(2), hold: true },                            // 前へ鋭く放つ (布が飛ぶ)
  { phase: 'active', at: 0, image: C(2), hold: true, tag: 'contact', hitIndex: 0 },         // 1発目: 布が伸びきって絡む
  { phase: 'comboGap', at: 0, image: C(3), hold: true, tag: 'followThrough', hitIndex: 0 }, // ぐいと引き戻す
  { phase: 'comboGap', at: 0.75, image: C(3), hold: true, hitIndex: 0, ...TENSE },
  { phase: 'active', at: 0, image: C(5), hold: true, tag: 'contact', hitIndex: 1, offset: { x: 6 }, scale: { x: 1.05, y: 0.95 }, rot: 2 }, // 2発目: 低く踏み込んで足もとを払う
  // 10-28 締めは五連と同じく段を踏んで立ち上がる (払いの低さから立ち姿へ一足に飛ばない)。
  { phase: 'recovery', at: 0, image: C(5), hold: true, tag: 'followThrough', offset: { x: 10 }, rot: 3, ease: 'easeOut' },
  { phase: 'recovery', at: 0.3, image: 'm1020_v6_g2', hold: true, tag: 'recover', ease: 'easeOut', soft: true },   // 10-28 低い踏み込みから半ば立ち上がる絵を挟む (剣を掲げる s1 へ一足に跳ねない)
  { phase: 'recovery', at: 0.5, image: SETTLE, hold: true, ease: 'easeOut', soft: true },
  { phase: 'recovery', at: 0.7, image: STAND, hold: true, ease: 'easeOut' },
  { phase: 'recovery', at: 0.9, image: 'idle', hold: true },
], { telegraph: CHARGE_MS, recovery: RECOVERY_MS })

// 払いは腰の後ろ (−45, 71) から剣先 (190, 21) まで、腰の高さを平らに流れる弧 (中心を床の下に置いて平らにした)。布の1発目は跡を描かない (布そのものが見える)。
export const BLADE_PULL_SMEAR: Record<string, ContactSmearArc> = {
  [C(5)]: { ...BLADE_SMEAR_BASE, anchorFwdPx: 30, anchorUpPx: -150, radiusPx: 234.2, angleFromRad: 1.9, angleToRad: 0.8186, widthPx: 8 },
}

const DEF: AttackDef = {
  id: BLADE_PULL_ID,
  label: '引き寄せ',
  kind: 'physical',
  chargeMs: CHARGE_MS, activeMs: ACTIVE_MS, recoveryMs: RECOVERY_MS,
  parryable: true, defense: 'parry',
  damage: PLAYER_DAMAGE.standard, postureDamage: 24,
  comboHits: 2, comboGapMs: BLADE_PULL_GAPS_MS[0], comboGapsMs: [...BLADE_PULL_GAPS_MS],
  range: RIBBON_REACH_PX, approachDistancePx: APPROACH_PX,
  strike: { ...BLADE_PULL_STRIKES[0] },
  comboStages: [
    // 布: 当たると傷を負わせずに手もとへ引き寄せる。弾かれた時の体幹の削りは小さい。
    { strike: { ...BLADE_PULL_STRIKES[0] }, postureDamage: 10, grab: { ...BLADE_PULL_GRAB } },
    { strike: { ...BLADE_PULL_STRIKES[1] } },
  ],
  // 踏み込まない (布で届かせる)。払いも引き寄せた所でその場で振る。
  comboLunge: { followHits: 0, followSpeed: 0, advanceSpeed: 0, plantFromHit: 0, gapOnly: true },
  sourceBossId: BOSS_ID,
}

let installed = false

/** 剣舞に「引き寄せ」を足す (DEV ?bladePull=1)。最初から出る技の組に入れる。 */
export function useBladeDancerPull(trail: BladeTrailStyle = 'afterimage'): void {
  if (!installed) {
    ATTACKS[BLADE_PULL_ID] = { ...DEF }
    const def = BOSS_DEFINITIONS.find(b => b.id === BOSS_ID)!
    def.baseAttackIds.push(BLADE_PULL_ID)
    def.unlockByExecutes?.[0]?.ids.push(BLADE_PULL_ID)
    installed = true
  }
  BOSS_ATTACK_TIMELINES[BOSS_ID] = { ...(BOSS_ATTACK_TIMELINES[BOSS_ID] ?? {}), [BLADE_PULL_ID]: BLADE_PULL_TIMELINE }
  Object.assign(SMEAR_ARTS, bladeDancerV6Smear(trail, BLADE_PULL_SMEAR))
  preloadBladePetals()
}

/** 外す (テストの後始末用)。 */
export function restoreBladeDancerPull(): void {
  if (!installed) return
  delete ATTACKS[BLADE_PULL_ID]
  const def = BOSS_DEFINITIONS.find(b => b.id === BOSS_ID)!
  def.baseAttackIds = def.baseAttackIds.filter(id => id !== BLADE_PULL_ID)
  const first = def.unlockByExecutes?.[0]
  if (first) first.ids = first.ids.filter(id => id !== BLADE_PULL_ID)
  delete BOSS_ATTACK_TIMELINES[BOSS_ID]?.[BLADE_PULL_ID]
  for (const k of Object.keys(BLADE_PULL_SMEAR)) delete SMEAR_ARTS[k]
  installed = false
}

export function parseBladePull(v: string | null): boolean {
  return v === '1'
}
